import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ToastService } from './../services/toast.service';
import { SpinnerService } from './../spinner/spinner.service';
import { tap, catchError, map } from 'rxjs/operators';
import { SetPasswordDTO, UserAccountClient } from './../models/models.swagger.generated';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { interval, of, Subscription } from 'rxjs';
import { loginRoute } from 'src/app/core/routing/route-names';
import { ErrorType } from 'src/app/core/routing/error-types';
import { AppErrorMessagesService } from '../services/app-error-messages.service';
import { AppErrorMessagesComponent } from '../error-messages/app-error-messages.component';
import { PasswordVisibilityDirective } from '../directives/password-visibility.directive';
import { MarkAsteriskDirective } from '../directives/mark-asterisk.directive';


@Component({
    selector: 'app-set-password',
    templateUrl: './set-password.component.html',
    styleUrls: ['./set-password.component.scss'],
    imports: [ReactiveFormsModule, MarkAsteriskDirective, PasswordVisibilityDirective, AppErrorMessagesComponent, RouterLink, TranslateModule]
})

export class SetPasswordComponent implements OnInit, OnDestroy {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private userAccountClient: UserAccountClient,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private errorMessageService: AppErrorMessagesService,
    private translateService: TranslateService,
    private router: Router
  ) { }

  public setPasswordForm: UntypedFormGroup;
  public passwordSetSuccess = false;
  public loginRoute = loginRoute;
  public autoRedirectMessage: string;
  private autoRedirectTimeInSeconds = 5;
  private userId: string;
  private verificationToken: string;
  private forgotPasswordProcess: boolean;
  private timeOutSubscription: Subscription;

  public get formFields() {
    return this.setPasswordForm.controls;
  }

  public get areParametersValid(): boolean {
    if ((this.userId) && (this.verificationToken)) {
      return true;
    }
    else {
      return false;
    }
  }

  public setPassword() {
    if (this.setPasswordForm.valid) {
      const formData = this.setPasswordForm.value;
      const setPasswordDTO: SetPasswordDTO = {
        newPassword: formData.password,
        userId: this.userId,
        verificationToken: this.verificationToken,
        forgotPasswordProcess: this.forgotPasswordProcess
      } as SetPasswordDTO;

      this.spinnerService.show();
      this.userAccountClient.setPassword(setPasswordDTO).pipe(
        tap(
          () => {
            this.spinnerService.hide();
            this.passwordSetSuccess = true;
            this.redirectToLogin();
          }),
        catchError((error: any) => {
          this.spinnerService.hide();
          this.handleError(error);
          return of(error);
        })).subscribe();
    }
  }

  private redirectToLogin() {
    this.timeOutSubscription = interval(1000).pipe(
      map((x) => {
        this.autoRedirectTimeInSeconds--;
        if (this.autoRedirectTimeInSeconds > 0) {
          this.autoRedirectMessage = this.translateService.instant('i18n.base.set-password.redirect-message',
            { params: this.autoRedirectTimeInSeconds });
        }
        else {
          this.router.navigate([this.loginRoute]);
        }
      })
    ).subscribe();
  }

  private handleError(error: any) {
    const apiErrorCode = this.errorMessageService.getApiErrorCode(error);
    let i18nErrorMessage = 'i18n.errors.server-error.message';
    const i18nErrorTitle = 'i18n.errors.server-error.title';
    if (apiErrorCode) {
      i18nErrorMessage = 'i18n.base.set-password.errors.' + apiErrorCode;
    }
    this.toastService.showError(i18nErrorTitle, i18nErrorMessage,
      null, ErrorType.serverError);
  }

  private initializeForm() {
    this.setPasswordForm = this.formBuilder.group({
      password: ['', Validators.required],
      passwordConfirm: ['', Validators.required]
    },
      {
        validators: [PasswordConfirmCompare]
      });
  }

  ngOnInit() {
    this.initializeForm();
    this.route.queryParams.subscribe(params => {
      this.userId = params.userId;
      this.verificationToken = params.code;
      this.forgotPasswordProcess = params.fp ? true : false;
    });
  }

  ngOnDestroy(): void {
    if (this.timeOutSubscription) {
      this.timeOutSubscription.unsubscribe();
    }
  }
}

export function PasswordConfirmCompare(control: AbstractControl): { [key: string]: boolean } {
  const password = control.get('password');
  const confirmPassword = control.get('passwordConfirm');
  if (!password || !confirmPassword) { return null; }
  const valid = confirmPassword.value === password.value;
  if (!valid) {
    confirmPassword.setErrors({ passwordsMustMatch: true });
  } else {
    confirmPassword.setErrors(null);
  }
  return valid ? null : { passwordsMustMatch: true };
}

